import { useEffect, useState } from "react";
import { Save, Clock, CreditCard, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const FIELDS = [
  { section: "Pré-réservations", icon: Clock, items: [
    { key: "pre_reservation_hold_hours", label: "Durée de protection d'une pré-réservation (heures)", hint: "48 h actuellement — modifiable (24 h, 36 h, 72 h…). Toutes les expirations utilisent ce réglage central." },
    { key: "finalization_deadline_hours", label: "Délai de finalisation client après validation (heures)", hint: "Passé ce délai, les quantités sont libérées automatiquement." },
  ]},
  { section: "Règles commerciales", icon: CreditCard, items: [
    { key: "deposit_rate", label: "Taux d'acompte (0–1)", hint: "0,30 = acompte 30 % — utilisé partout (sélection, réservation, admin).", step: "0.01" },
    { key: "balance_deadline_days", label: "Échéance du solde (jours avant l'événement)", hint: "7 = solde au plus tard J-7." },
  ]},
  { section: "Livraison", icon: Truck, items: [
    { key: "delivery.free_km", label: "Kilomètres offerts", hint: "20 km offerts actuellement." },
    { key: "delivery.price_per_km_roundtrip", label: "Prix par km aller-retour au-delà (€)", hint: "1,10 €/km A-R actuellement.", step: "0.01" },
    { key: "delivery.max_km", label: "Distance maximale (km)", hint: "80 km autour d'Artres — au-delà : étude exceptionnelle, jamais de blocage brutal." },
    { key: "delivery.free_threshold_material", label: "Livraison offerte à partir de (€ de matériel)", hint: "250 € actuellement." },
  ]},
];

const SOCIAL_FIELDS = [
  { key: "facebook_url", label: "URL Facebook", hint: "Lien complet de la page Facebook — vide = bouton masqué sur le site." },
  { key: "instagram_url", label: "URL Instagram", hint: "Lien complet du profil Instagram — vide = bouton masqué sur le site." },
];

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [values, setValues] = useState({});
  const [social, setSocial] = useState({ facebook_url: "", instagram_url: "" });
  const [savingSocial, setSavingSocial] = useState(false);
  const [saving, setSaving] = useState(false);

  const getVal = (obj, path) => path.split(".").reduce((o, k) => o?.[k], obj);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      setSettings(r.data);
      const flat = {};
      FIELDS.forEach((s) => s.items.forEach((f) => { flat[f.key] = getVal(r.data, f.key); }));
      setValues(flat);
      setSocial({ facebook_url: r.data.facebook_url || "", instagram_url: r.data.instagram_url || "" });
    }).catch(() => toast.error("Impossible de charger les réglages"));
  }, []);

  const saveSocial = async () => {
    setSavingSocial(true);
    try {
      await api.patch("/admin/settings", { facebook_url: social.facebook_url, instagram_url: social.instagram_url });
      toast.success("Liens sociaux enregistrés — appliqués immédiatement sur le site");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingSocial(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      FIELDS.forEach((s) => s.items.forEach((f) => {
        const raw = values[f.key];
        const num = Number(raw);
        if (Number.isNaN(num)) return;
        if (f.key.startsWith("delivery.")) {
          payload.delivery = payload.delivery || {};
          payload.delivery[f.key.split(".")[1]] = num;
        } else {
          payload[f.key] = num;
        }
      }));
      const { data } = await api.patch("/admin/settings", payload);
      setSettings(data);
      toast.success("Réglages enregistrés — effectifs immédiatement, historisés dans l'audit");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <div className="py-16 text-center font-heading text-deepgreen" data-testid="settings-loading">Chargement…</div>;

  return (
    <div data-testid="admin-settings-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline-ae">B21 — Réglages du site</p>
          <h1 className="mt-1 font-heading text-2xl sm:text-3xl text-deepgreen">Réglages commerciaux centraux</h1>
          <p className="mt-1 text-sm text-muted-foreground">Source unique des règles métier — aucune valeur n'est codée en dur ailleurs.</p>
        </div>
        <button type="button" onClick={save} disabled={saving} className="btn-primary-ae !px-5 !py-2.5 !text-xs disabled:opacity-50" data-testid="settings-save">
          <Save className="h-4 w-4" /> {saving ? "Enregistrement…" : "Enregistrer les réglages"}
        </button>
      </div>

      <div className="mt-6 space-y-6">
        {FIELDS.map((section) => (
          <div key={section.section} className="card-ae p-6" data-testid={`settings-section-${section.section.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <h2 className="flex items-center gap-2 font-heading text-lg text-deepgreen">
              <section.icon className="h-5 w-5 text-sage" /> {section.section}
            </h2>
            <div className="mt-4 grid gap-5 md:grid-cols-2">
              {section.items.map((f) => (
                <div key={f.key}>
                  <label className="label-ae" htmlFor={`set-${f.key}`}>{f.label}</label>
                  <input
                    id={`set-${f.key}`}
                    type="number"
                    step={f.step || "1"}
                    min="0"
                    className="input-ae"
                    value={values[f.key] ?? ""}
                    onChange={(e) => setValues((prev) => ({ ...prev, [f.key]: e.target.value }))}
                    data-testid={`setting-${f.key.replace(/\./g, "-")}`}
                  />
                  <p className="mt-1 text-xs text-muted-foreground">{f.hint}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-lg bg-sage-light p-4 text-sm text-muted-foreground" data-testid="settings-note">
        Toute modification est enregistrée dans le journal d'audit (qui, quand, ancienne → nouvelle valeur).
      </div>

      <div className="card-ae mt-6 p-6" data-testid="settings-section-reseaux-sociaux">
        <h2 className="flex items-center gap-2 font-heading text-lg text-deepgreen">Réseaux sociaux</h2>
        <p className="mt-1 text-xs text-muted-foreground">Ces liens alimentent automatiquement les boutons Facebook / Instagram du site. Laisser vide pour masquer le bouton.</p>
        <div className="mt-4 grid gap-5 md:grid-cols-2">
          {SOCIAL_FIELDS.map((f) => (
            <div key={f.key}>
              <label className="label-ae" htmlFor={`set-${f.key}`}>{f.label}</label>
              <input
                id={`set-${f.key}`}
                type="url"
                placeholder="https://…"
                className="input-ae"
                value={social[f.key]}
                onChange={(e) => setSocial((prev) => ({ ...prev, [f.key]: e.target.value }))}
                data-testid={`setting-${f.key.replace(/_/g, "-")}`}
              />
              <p className="mt-1 text-xs text-muted-foreground">{f.hint}</p>
            </div>
          ))}
        </div>
        <button type="button" onClick={saveSocial} disabled={savingSocial} className="btn-primary-ae mt-4 !px-5 !py-2.5 !text-xs disabled:opacity-50" data-testid="settings-save-social">
          <Save className="h-4 w-4" /> {savingSocial ? "Enregistrement…" : "Enregistrer les liens sociaux"}
        </button>
      </div>
    </div>
  );
}
